import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, Plus, Trash2, Upload, Image, Star, 
  Users, Loader2, Edit2, Save, X, FolderInput, Copy, Merge, CheckSquare, UserPlus, Mail
} from "lucide-react";
import SmartImportModal from "@/components/SmartImportModal";
import GuidanceModal from "@/components/GuidanceModal";
import { uploadFileToS3 } from "@/utils/s3Upload";

import { getApiUrl, getApiBaseUrl } from '@/config/api';

const API = getApiUrl();

const getAssetUrl = (fileUrl) => {
  if (!fileUrl) return '';
  const backendUrl = getApiBaseUrl();
  
  // Handle old absolute URLs - extract the path and use current backend
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    const apiMatch = fileUrl.match(/\/api\/(files|exports)\/(.+)/);
    if (apiMatch) {
      return `${backendUrl}/api/${apiMatch[1]}/${apiMatch[2]}`;
    }
    return fileUrl;
  }
  
  if (fileUrl.startsWith('/api/')) {
    return `${backendUrl}${fileUrl}`;
  }
  return `${backendUrl}/api/${fileUrl.replace(/^\//, '')}`;
};

export default function Characters() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [characters, setCharacters] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  
  // Actors for assignment
  const [myActors, setMyActors] = useState([]);
  const [showAssignActorDialog, setShowAssignActorDialog] = useState(false);
  const [selectedActorForAssign, setSelectedActorForAssign] = useState("");
  const [assigningActor, setAssigningActor] = useState(false);
  
  // Multi-select for merge
  const [selectMode, setSelectMode] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState(new Set());
  
  // Merge dialog
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [merging, setMerging] = useState(false);
  
  // Smart Import Modal
  const [showSmartImport, setShowSmartImport] = useState(false);
  const [smartImportFiles, setSmartImportFiles] = useState([]);
  const [characterUploadProgress, setCharacterUploadProgress] = useState(null);
  
  // Create character dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCharacterName, setNewCharacterName] = useState("");
  const [newCharacterDescription, setNewCharacterDescription] = useState("");
  const [creating, setCreating] = useState(false);
  
  // Edit character
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Upload image
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadForCharacter, setUploadForCharacter] = useState(null);
  
  // Import character dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importableCharacters, setImportableCharacters] = useState([]);
  const [loadingImportable, setLoadingImportable] = useState(false);
  const [selectedImportProject, setSelectedImportProject] = useState("");
  const [selectedImportCharacter, setSelectedImportCharacter] = useState("");
  const [importing, setImporting] = useState(false);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Guidance modal for empty state
  const [showGuidance, setShowGuidance] = useState(false);
  const [hasCheckedCharacters, setHasCheckedCharacters] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchCharacters();
    fetchMyActors();
  }, [projectId]);

  // Show guidance modal when no characters exist (after initial load)
  useEffect(() => {
    if (!loading && hasCheckedCharacters && characters.length === 0) {
      setShowGuidance(true);
    }
  }, [loading, hasCheckedCharacters, characters.length]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProject(response.data);
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchCharacters = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/characters`, {
        headers: getAuthHeaders()
      });
      // Sort alphabetically
      const sorted = (response.data || []).sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      setCharacters(sorted);
      setHasCheckedCharacters(true);
    } catch (error) {
      console.error("Failed to load characters:", error);
      setHasCheckedCharacters(true);
    }
  };

  const fetchMyActors = async () => {
    try {
      const response = await axios.get(`${API}/director/my-actors`, {
        headers: getAuthHeaders()
      });
      setMyActors(response.data.actors || []);
    } catch (error) {
      console.error("Failed to load actors:", error);
    }
  };

  // Get actors assigned to a character
  const getAssignedActors = (character) => {
    if (!character?.assigned_actors) return [];
    return myActors.filter(actor => character.assigned_actors.includes(actor.id));
  };

  // Get actors NOT assigned to selected character
  const getAvailableActors = () => {
    if (!selectedCharacter) return myActors;
    return myActors.filter(actor => 
      !selectedCharacter.assigned_actors?.includes(actor.id)
    );
  };

  const handleAssignActor = async () => {
    if (!selectedActorForAssign || !selectedCharacter) return;
    
    setAssigningActor(true);
    try {
      await axios.post(`${API}/director/assign-actor`, {
        actor_id: selectedActorForAssign,
        character_id: selectedCharacter.id
      }, { headers: getAuthHeaders() });
      
      toast.success("Actor assigned to character");
      setShowAssignActorDialog(false);
      setSelectedActorForAssign("");
      
      // Refresh characters to get updated assigned_actors
      await fetchCharacters();
      
      // Update selected character
      const updated = characters.find(c => c.id === selectedCharacter.id);
      if (updated) {
        setSelectedCharacter({
          ...updated,
          assigned_actors: [...(updated.assigned_actors || []), selectedActorForAssign]
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign actor");
    } finally {
      setAssigningActor(false);
    }
  };

  const handleUnassignActor = async (actorId) => {
    if (!selectedCharacter) return;
    
    try {
      await axios.post(`${API}/director/unassign-actor`, {
        actor_id: actorId,
        character_id: selectedCharacter.id
      }, { headers: getAuthHeaders() });
      
      toast.success("Actor unassigned");
      
      // Update local state
      setSelectedCharacter({
        ...selectedCharacter,
        assigned_actors: (selectedCharacter.assigned_actors || []).filter(id => id !== actorId)
      });
      
      await fetchCharacters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign actor");
    }
  };

  // Smart Import handlers
  const handleBulkUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setSmartImportFiles(files);
    setShowSmartImport(true);
    
    // Reset input
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = "";
    }
  };

  const handleSmartImportConfirm = async (importData) => {
    // importData: [{ name, existingCharacterId, files }]
    let created = 0;
    let updated = 0;
    let imagesAdded = 0;
    const totalFiles = importData.reduce((sum, group) => sum + group.files.length, 0);
    let processedFiles = 0;
    
    // Initialize progress
    setCharacterUploadProgress({ current: 0, total: totalFiles, currentFile: '' });
    
    // Update progress helper
    const updateProgress = (filename) => {
      processedFiles++;
      setCharacterUploadProgress({ 
        current: processedFiles, 
        total: totalFiles, 
        currentFile: filename 
      });
    };
    
    for (const group of importData) {
      try {
        let characterId = group.existingCharacterId;
        
        // Create new character if doesn't exist
        if (!characterId) {
          const createResponse = await axios.post(`${API}/projects/${projectId}/characters`, {
            name: group.name,
            description: ""
          }, { headers: getAuthHeaders() });
          characterId = createResponse.data.id;
          created++;
        } else {
          updated++;
        }
        
        // Upload each file and add to character using direct S3 upload
        for (const file of group.files) {
          await uploadFileToS3(file, projectId, {
            type: 'character',
            name: file.name,
            characterProfileId: characterId
          });
          
          imagesAdded++;
          updateProgress(file.name);
        }
      } catch (error) {
        console.error(`Failed to import ${group.name}:`, error);
        toast.error(`Failed to import ${group.name}`);
      }
    }
    
    // Clear progress
    setCharacterUploadProgress(null);
    
    // Refresh characters
    await fetchCharacters();
    
    // Show success message
    const messages = [];
    if (created > 0) messages.push(`${created} new character${created !== 1 ? 's' : ''}`);
    if (updated > 0) messages.push(`${updated} updated`);
    messages.push(`${imagesAdded} image${imagesAdded !== 1 ? 's' : ''} added`);
    
    toast.success(`Import complete: ${messages.join(', ')}`);
    
    setShowSmartImport(false);
    setSmartImportFiles([]);
  };

  const handleCreateCharacter = async () => {
    if (!newCharacterName.trim()) {
      toast.error("Please enter a character name");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/characters`, {
        name: newCharacterName.trim(),
        description: newCharacterDescription.trim()
      }, { headers: getAuthHeaders() });
      
      setCharacters(prev => [...prev, response.data]);
      setShowCreateDialog(false);
      setNewCharacterName("");
      setNewCharacterDescription("");
      setSelectedCharacter(response.data);
      toast.success(`Character "${response.data.name}" created!`);
    } catch (error) {
      toast.error("Failed to create character");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCharacter = async () => {
    if (!editingCharacter || !editName.trim()) return;
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/characters/${editingCharacter.id}`, {
        name: editName.trim(),
        description: editDescription.trim()
      }, { headers: getAuthHeaders() });
      
      setCharacters(prev => prev.map(c => 
        c.id === editingCharacter.id ? response.data : c
      ));
      
      if (selectedCharacter?.id === editingCharacter.id) {
        setSelectedCharacter(response.data);
      }
      
      setEditingCharacter(null);
      toast.success("Character updated!");
    } catch (error) {
      toast.error("Failed to update character");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCharacter = async () => {
    if (!characterToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/characters/${characterToDelete.id}`, { headers: getAuthHeaders() });
      
      setCharacters(prev => prev.filter(c => c.id !== characterToDelete.id));
      
      if (selectedCharacter?.id === characterToDelete.id) {
        setSelectedCharacter(null);
      }
      
      setShowDeleteConfirm(false);
      setCharacterToDelete(null);
      toast.success("Character deleted");
    } catch (error) {
      toast.error("Failed to delete character");
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadForCharacter) return;
    
    setUploadingImage(true);
    try {
      // Upload using direct S3 upload
      const asset = await uploadFileToS3(file, projectId, {
        type: 'character',
        name: file.name,
        characterProfileId: uploadForCharacter.id
      });
      
      // Add to character profile
      const imageFormData = new FormData();
      imageFormData.append("asset_id", asset.id);
      imageFormData.append("is_primary", uploadForCharacter.reference_images?.length === 0 ? "true" : "false");
      
      await axios.post(
        `${API}/characters/${uploadForCharacter.id}/images`,
        imageFormData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      
      // Refresh characters
      await fetchCharacters();
      
      // Update selected character
      const updated = await axios.get(`${API}/characters/${uploadForCharacter.id}`, { headers: getAuthHeaders() });
      setSelectedCharacter(updated.data);
      
      toast.success("Image added to character");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
      setUploadForCharacter(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = async (characterId, assetId) => {
    try {
      await axios.delete(`${API}/characters/${characterId}/images/${assetId}`, { headers: getAuthHeaders() });
      
      // Refresh characters
      await fetchCharacters();
      
      // Update selected character
      const updated = await axios.get(`${API}/characters/${characterId}`, { headers: getAuthHeaders() });
      setSelectedCharacter(updated.data);
      
      toast.success("Image removed");
    } catch (error) {
      toast.error("Failed to remove image");
    }
  };

  const handleSetPrimaryImage = async (characterId, assetId) => {
    try {
      await axios.put(`${API}/characters/${characterId}/images/${assetId}/primary`, {}, { headers: getAuthHeaders() });
      
      // Refresh characters
      await fetchCharacters();
      
      // Update selected character
      const updated = await axios.get(`${API}/characters/${characterId}`, { headers: getAuthHeaders() });
      setSelectedCharacter(updated.data);
      
      toast.success("Primary image updated");
    } catch (error) {
      toast.error("Failed to set primary image");
    }
  };

  const handleOpenImportDialog = async () => {
    setShowImportDialog(true);
    setLoadingImportable(true);
    
    try {
      const response = await axios.get(`${API}/projects/${projectId}/characters/importable`, { headers: getAuthHeaders() });
      setImportableCharacters(response.data || []);
    } catch (error) {
      toast.error("Failed to load importable characters");
    } finally {
      setLoadingImportable(false);
    }
  };

  const handleImportCharacter = async () => {
    if (!selectedImportProject || !selectedImportCharacter) {
      toast.error("Please select a character to import");
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/characters/import`, {
        source_character_id: selectedImportCharacter,
        source_project_id: selectedImportProject
      }, { headers: getAuthHeaders() });
      
      setCharacters(prev => [...prev, response.data]);
      setShowImportDialog(false);
      setSelectedImportProject("");
      setSelectedImportCharacter("");
      setSelectedCharacter(response.data);
      toast.success(`Character "${response.data.name}" imported!`);
    } catch (error) {
      toast.error("Failed to import character");
    } finally {
      setImporting(false);
    }
  };

  const startEdit = (character) => {
    setEditingCharacter(character);
    setEditName(character.name);
    setEditDescription(character.description || "");
  };

  const cancelEdit = () => {
    setEditingCharacter(null);
    setEditName("");
    setEditDescription("");
  };

  // Merge functions
  const toggleSelectMode = () => {
    if (selectMode) {
      // Exiting select mode
      setSelectMode(false);
      setSelectedForMerge(new Set());
    } else {
      // Entering select mode
      setSelectMode(true);
      setSelectedCharacter(null);
    }
  };

  const toggleCharacterSelection = (characterId) => {
    setSelectedForMerge(prev => {
      const newSet = new Set(prev);
      if (newSet.has(characterId)) {
        newSet.delete(characterId);
      } else {
        newSet.add(characterId);
      }
      return newSet;
    });
  };

  const handleOpenMergeDialog = () => {
    if (selectedForMerge.size < 2) {
      toast.error("Select at least 2 characters to merge");
      return;
    }
    setMergeTargetId("");
    setShowMergeDialog(true);
  };

  const handleMergeCharacters = async () => {
    if (!mergeTargetId) {
      toast.error("Please select which character to keep");
      return;
    }
    
    setMerging(true);
    try {
      const sourceIds = Array.from(selectedForMerge).filter(id => id !== mergeTargetId);
      
      const response = await axios.post(`${API}/projects/${projectId}/characters/merge`, {
        target_character_id: mergeTargetId,
        source_character_ids: sourceIds
      }, { headers: getAuthHeaders() });
      
      // Refresh characters
      await fetchCharacters();
      
      // Select the merged character
      setSelectedCharacter(response.data.merged_character);
      
      // Exit select mode
      setSelectMode(false);
      setSelectedForMerge(new Set());
      setShowMergeDialog(false);
      
      toast.success(`Merged ${response.data.characters_merged} characters (${response.data.images_added} images added)`);
    } catch (error) {
      toast.error("Failed to merge characters");
    } finally {
      setMerging(false);
    }
  };

  const getPrimaryImage = (character) => {
    const primary = character.reference_images?.find(img => img.is_primary);
    return primary || character.reference_images?.[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hidden file input for single image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleUploadImage}
      />
      
      {/* Hidden file input for bulk import */}
      <input
        ref={bulkFileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="hidden"
        onChange={handleBulkUpload}
      />
      
      {/* Smart Import Modal */}
      <SmartImportModal
        open={showSmartImport}
        onClose={() => {
          setShowSmartImport(false);
          setSmartImportFiles([]);
          setCharacterUploadProgress(null);
        }}
        files={smartImportFiles}
        existingCharacters={characters}
        onConfirm={handleSmartImportConfirm}
        projectId={projectId}
        uploadProgress={characterUploadProgress}
      />
      
      {/* Guidance Modal for empty state */}
      <GuidanceModal
        open={showGuidance}
        onClose={() => setShowGuidance(false)}
        type="no-characters"
        projectId={projectId}
      />
      
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/project/${projectId}`)}
              className="text-zinc-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">Characters</h1>
              <p className="text-sm text-zinc-400">{project?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Select/Merge Mode Toggle */}
            {characters.length >= 2 && (
              <Button
                variant={selectMode ? "default" : "outline"}
                size="sm"
                onClick={toggleSelectMode}
                className={selectMode 
                  ? "bg-amber-600 hover:bg-amber-700" 
                  : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                }
                data-testid="select-mode-btn"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectMode ? "Cancel" : "Select to Merge"}
              </Button>
            )}
            
            {/* Merge Button - Only shown in select mode with 2+ selected */}
            {selectMode && selectedForMerge.size >= 2 && (
              <Button
                size="sm"
                onClick={handleOpenMergeDialog}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="merge-btn"
              >
                <Merge className="w-4 h-4 mr-2" />
                Merge ({selectedForMerge.size})
              </Button>
            )}
            
            {!selectMode && (
              <>
                {/* Import Images (Smart Import) */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkFileInputRef.current?.click()}
                  className="border-green-700 text-green-400 hover:bg-green-900/30"
                  data-testid="import-images-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Images
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenImportDialog}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  data-testid="import-character-btn"
                >
                  <FolderInput className="w-4 h-4 mr-2" />
                  Import from Project
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-character-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Character
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Character List */}
          <div className="lg:col-span-1">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Character Profiles
                </CardTitle>
                <CardDescription>
                  {characters.length} character{characters.length !== 1 ? 's' : ''} in this project
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[calc(100vh-280px)]">
                  {characters.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No characters yet</p>
                      <p className="text-sm mt-1">Create or import a character to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {characters.map(character => {
                        const primaryImage = getPrimaryImage(character);
                        const isSelected = selectedCharacter?.id === character.id;
                        const isSelectedForMerge = selectedForMerge.has(character.id);
                        
                        return (
                          <div
                            key={character.id}
                            onClick={() => {
                              if (selectMode) {
                                toggleCharacterSelection(character.id);
                              } else {
                                setSelectedCharacter(character);
                              }
                            }}
                            className={`
                              flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all
                              ${selectMode && isSelectedForMerge
                                ? 'bg-purple-600/20 border border-purple-500/50'
                                : isSelected && !selectMode
                                  ? 'bg-blue-600/20 border border-blue-500/50' 
                                  : 'bg-zinc-800/50 border border-transparent hover:bg-zinc-800'
                              }
                            `}
                            data-testid={`character-item-${character.id}`}
                          >
                            {/* Checkbox for select mode */}
                            {selectMode && (
                              <div 
                                className={`
                                  w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                                  ${isSelectedForMerge 
                                    ? 'bg-purple-600 border-purple-600' 
                                    : 'border-zinc-600'
                                  }
                                `}
                              >
                                {isSelectedForMerge && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            )}
                            
                            {/* Thumbnail */}
                            <div className="w-12 h-12 rounded-lg bg-zinc-700 overflow-hidden flex-shrink-0">
                              {primaryImage ? (
                                <img
                                  src={getAssetUrl(primaryImage.thumbnail_url || primaryImage.file_url)}
                                  alt={character.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Users className="w-6 h-6 text-zinc-500" />
                                </div>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{character.name}</p>
                              <p className="text-xs text-zinc-500">
                                {character.reference_images?.length || 0} image{(character.reference_images?.length || 0) !== 1 ? 's' : ''}
                              </p>
                            </div>
                            
                            {/* Indicator */}
                            {!selectMode && isSelected && (
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Character Details */}
          <div className="lg:col-span-2">
            {selectedCharacter ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {editingCharacter?.id === selectedCharacter.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Character name"
                            className="bg-zinc-800 border-zinc-700 text-lg font-semibold"
                            data-testid="edit-character-name"
                          />
                          <Textarea
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            placeholder="Character description (optional)"
                            className="bg-zinc-800 border-zinc-700 resize-none"
                            rows={2}
                            data-testid="edit-character-description"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleUpdateCharacter}
                              disabled={saving}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="save-character-btn"
                            >
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEdit}
                              className="text-zinc-400"
                              data-testid="cancel-edit-btn"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <CardTitle className="text-xl">{selectedCharacter.name}</CardTitle>
                          {selectedCharacter.description && (
                            <CardDescription className="mt-1">
                              {selectedCharacter.description}
                            </CardDescription>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                              <Copy className="w-3 h-3 mr-1" />
                              {selectedCharacter.trigger_word}
                            </Badge>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {editingCharacter?.id !== selectedCharacter.id && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => startEdit(selectedCharacter)}
                          className="text-zinc-400 hover:text-white"
                          data-testid="edit-character-btn"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCharacterToDelete(selectedCharacter);
                            setShowDeleteConfirm(true);
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid="delete-character-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Reference Images Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-zinc-300">Reference Images</h3>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setUploadForCharacter(selectedCharacter);
                          fileInputRef.current?.click();
                        }}
                        disabled={uploadingImage}
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                        data-testid="upload-image-btn"
                      >
                        {uploadingImage && uploadForCharacter?.id === selectedCharacter.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Add Image
                      </Button>
                    </div>
                    
                    {selectedCharacter.reference_images?.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {selectedCharacter.reference_images.map((img, idx) => (
                          <div
                            key={`${selectedCharacter.id}-${img.asset_id}-${idx}`}
                            className={`
                              relative group rounded-lg overflow-hidden border-2 transition-all
                              ${img.is_primary ? 'border-amber-500' : 'border-zinc-700 hover:border-zinc-600'}
                            `}
                          >
                            <div className="aspect-square bg-zinc-800">
                              <img
                                src={getAssetUrl(img.file_url)}
                                alt={`Reference ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Primary badge */}
                            {img.is_primary && (
                              <div className="absolute top-1 left-1 bg-amber-500 text-black text-xs px-1.5 py-0.5 rounded font-medium">
                                Primary
                              </div>
                            )}
                            
                            {/* Hover actions */}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              {!img.is_primary && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleSetPrimaryImage(selectedCharacter.id, img.asset_id)}
                                  className="w-8 h-8 text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
                                  title="Set as primary"
                                  data-testid={`set-primary-${img.asset_id}`}
                                >
                                  <Star className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleRemoveImage(selectedCharacter.id, img.asset_id)}
                                className="w-8 h-8 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                title="Remove image"
                                data-testid={`remove-image-${img.asset_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border border-dashed border-zinc-700 rounded-lg">
                        <Image className="w-10 h-10 mx-auto mb-2 text-zinc-600" />
                        <p className="text-zinc-500 text-sm">No reference images yet</p>
                        <p className="text-zinc-600 text-xs mt-1">Upload images to use in scenes</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Assigned Actors Section */}
                  <div className="mt-6 pt-6 border-t border-zinc-800">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-zinc-300">Assigned Actors</h3>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAssignActorDialog(true)}
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                        data-testid="assign-actor-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assign Actor
                      </Button>
                    </div>
                    
                    {getAssignedActors(selectedCharacter).length > 0 ? (
                      <div className="space-y-2">
                        {getAssignedActors(selectedCharacter).map(actor => (
                          <div 
                            key={actor.id}
                            className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg"
                            data-testid={`assigned-actor-${actor.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                                <Users className="w-4 h-4 text-zinc-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {actor.name || actor.email}
                                </p>
                                <p className="text-xs text-zinc-500">{actor.email}</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnassignActor(actor.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`unassign-actor-${actor.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 border border-dashed border-zinc-700 rounded-lg">
                        <Users className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
                        <p className="text-zinc-500 text-sm">No actors assigned</p>
                        <p className="text-zinc-600 text-xs mt-1">
                          {myActors.length > 0 
                            ? "Click 'Assign Actor' to add voice actors" 
                            : "Invite actors from Cast & Crew page first"}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-16 text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg font-medium text-zinc-400">Select a Character</h3>
                  <p className="text-sm text-zinc-500 mt-1">
                    Choose a character from the list to view and edit their profile
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Character Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Create New Character</DialogTitle>
            <DialogDescription>
              Add a new character profile to this project
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Character Name</Label>
              <Input
                value={newCharacterName}
                onChange={(e) => setNewCharacterName(e.target.value)}
                placeholder="e.g., Dean, Sarah, Dr. Smith"
                className="bg-zinc-800 border-zinc-700"
                data-testid="new-character-name"
              />
              <p className="text-xs text-zinc-500">
                Use the name as it appears in your script
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={newCharacterDescription}
                onChange={(e) => setNewCharacterDescription(e.target.value)}
                placeholder="Brief character description..."
                className="bg-zinc-800 border-zinc-700 resize-none"
                rows={3}
                data-testid="new-character-description"
              />
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleCreateCharacter}
              disabled={creating || !newCharacterName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-create-character"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Character
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Character Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Import Character from Project</DialogTitle>
            <DialogDescription>
              Import a character profile with all its settings and images
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {loadingImportable ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                <p className="text-sm text-zinc-500 mt-2">Loading available characters...</p>
              </div>
            ) : importableCharacters.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 mx-auto mb-2 text-zinc-600" />
                <p className="text-zinc-500">No characters available to import</p>
                <p className="text-sm text-zinc-600 mt-1">Create characters in other projects first</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Select Project</Label>
                  <Select
                    value={selectedImportProject}
                    onValueChange={(value) => {
                      setSelectedImportProject(value);
                      setSelectedImportCharacter("");
                    }}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="import-project-select">
                      <SelectValue placeholder="Choose a project" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {importableCharacters.map(proj => (
                        <SelectItem key={proj.project_id} value={proj.project_id}>
                          {proj.project_name} ({proj.characters.length} characters)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedImportProject && (
                  <div className="space-y-2">
                    <Label>Select Character</Label>
                    <Select
                      value={selectedImportCharacter}
                      onValueChange={setSelectedImportCharacter}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="import-character-select">
                        <SelectValue placeholder="Choose a character" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {importableCharacters
                          .find(p => p.project_id === selectedImportProject)
                          ?.characters.map(char => (
                            <SelectItem key={char.id} value={char.id}>
                              {char.name} ({char.reference_images?.length || 0} images)
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleImportCharacter}
              disabled={importing || !selectedImportCharacter}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-import-character"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FolderInput className="w-4 h-4 mr-2" />}
              Import Character
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Character</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{characterToDelete?.name}&rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleDeleteCharacter}
              disabled={deleting}
              variant="destructive"
              data-testid="confirm-delete-character"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Character
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Characters Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-purple-400">Merge Characters</DialogTitle>
            <DialogDescription>
              Select which character to keep. All images from other characters will be merged into it, and the others will be deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Keep this character (others will merge into it)</Label>
              <Select
                value={mergeTargetId}
                onValueChange={setMergeTargetId}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="merge-target-select">
                  <SelectValue placeholder="Select the character to keep" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {characters
                    .filter(c => selectedForMerge.has(c.id))
                    .map(char => (
                      <SelectItem key={char.id} value={char.id}>
                        {char.name} ({char.reference_images?.length || 0} images)
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3 text-sm">
              <p className="text-zinc-400 mb-2">Characters to merge:</p>
              <div className="flex flex-wrap gap-2">
                {characters
                  .filter(c => selectedForMerge.has(c.id))
                  .map(char => (
                    <Badge 
                      key={char.id}
                      variant="outline" 
                      className={`
                        ${char.id === mergeTargetId 
                          ? 'border-purple-500 text-purple-300 bg-purple-500/10' 
                          : 'border-zinc-600 text-zinc-400'
                        }
                      `}
                    >
                      {char.name}
                      {char.id === mergeTargetId && " (keep)"}
                    </Badge>
                  ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleMergeCharacters}
              disabled={merging || !mergeTargetId}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-merge-characters"
            >
              {merging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Merge className="w-4 h-4 mr-2" />}
              Merge Characters
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Actor Dialog */}
      <Dialog open={showAssignActorDialog} onOpenChange={setShowAssignActorDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Assign Actor to {selectedCharacter?.name}</DialogTitle>
            <DialogDescription>
              Select a voice actor to assign to this character.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {getAvailableActors().length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-10 h-10 mx-auto mb-2 text-zinc-600" />
                {myActors.length === 0 ? (
                  <>
                    <p className="text-zinc-400">No actors invited yet</p>
                    <p className="text-sm text-zinc-500 mt-1">
                      Go to Cast & Crew to invite actors
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/project/${projectId}/team`)}
                      className="mt-3 border-zinc-700 text-zinc-300"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Go to Cast & Crew
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-zinc-400">All actors already assigned</p>
                    <p className="text-sm text-zinc-500 mt-1">
                      Invite more actors from Cast & Crew page
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Select Actor</Label>
                <Select
                  value={selectedActorForAssign}
                  onValueChange={setSelectedActorForAssign}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="assign-actor-select">
                    <SelectValue placeholder="Choose an actor..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {getAvailableActors().map(actor => (
                      <SelectItem key={actor.id} value={actor.id}>
                        {actor.name || actor.email}
                        {actor.name && <span className="text-zinc-500 ml-2">({actor.email})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleAssignActor}
              disabled={assigningActor || !selectedActorForAssign}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-assign-actor"
            >
              {assigningActor ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Assign Actor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
